type ServiceWorkerLike = {
  addEventListener: (type: "statechange", listener: () => void) => void;
  postMessage: (message: unknown) => void;
  state: string;
};

type ServiceWorkerRegistrationLike = {
  addEventListener: (type: "updatefound", listener: () => void) => void;
  installing: ServiceWorkerLike | null;
  update: () => Promise<unknown>;
  waiting: ServiceWorkerLike | null;
};

type ServiceWorkerContainerLike = {
  addEventListener: (type: "controllerchange", listener: () => void) => void;
  controller: object | null;
  register: (
    scriptURL: string,
    options: RegistrationOptions,
  ) => Promise<ServiceWorkerRegistrationLike>;
};

type BrowserWindowLike = {
  addEventListener: (type: "load", listener: () => void, options?: AddEventListenerOptions) => void;
  document: Pick<Document, "readyState">;
  location?: Pick<Location, "reload">;
  Capacitor?: {
    isNativePlatform?: () => boolean;
  };
};

const skipWaitingMessage = { type: "SKIP_WAITING" };

export type ServiceWorkerRegistrationEnvironment = {
  browserWindow: BrowserWindowLike | undefined;
  serviceWorker: ServiceWorkerContainerLike | undefined;
  basePath: string;
};

function normalizeBasePath(basePath: string): string {
  return basePath.endsWith("/") ? basePath : `${basePath}/`;
}

function serviceWorkerPath(basePath: string): string {
  return `${normalizeBasePath(basePath)}service-worker.js`;
}

export function isNativeShell(browserWindow: BrowserWindowLike | undefined): boolean {
  return browserWindow?.Capacitor?.isNativePlatform?.() ?? false;
}

export function registerPassoServiceWorker(
  environment: ServiceWorkerRegistrationEnvironment = {
    browserWindow: window,
    serviceWorker: navigator.serviceWorker,
    basePath: import.meta.env.BASE_URL,
  },
): void {
  const { browserWindow, serviceWorker, basePath } = environment;

  if (browserWindow === undefined || serviceWorker === undefined || isNativeShell(browserWindow)) {
    return;
  }

  const controlledAtStartup = serviceWorker.controller !== null;
  let reloadStarted = false;

  serviceWorker.addEventListener("controllerchange", () => {
    if (!controlledAtStartup || reloadStarted) return;

    reloadStarted = true;
    browserWindow.location?.reload();
  });

  const activateUpdate = (worker: ServiceWorkerLike | null): void => {
    if (worker === null || serviceWorker.controller === null) {
      return;
    }

    worker.postMessage(skipWaitingMessage);
  };

  const monitorInstallingWorker = (worker: ServiceWorkerLike | null): void => {
    if (worker === null) return;
    if (worker.state === "installed") {
      activateUpdate(worker);
      return;
    }

    worker.addEventListener("statechange", () => {
      if (worker.state === "installed") activateUpdate(worker);
    });
  };

  const monitorRegistration = (registration: ServiceWorkerRegistrationLike): void => {
    activateUpdate(registration.waiting);
    monitorInstallingWorker(registration.installing);

    registration.addEventListener("updatefound", () => {
      monitorInstallingWorker(registration.installing);
    });

    void registration.update().catch(() => {
      // The current worker remains usable if an update check cannot complete.
    });
  };

  const register = () => {
    void serviceWorker
      .register(serviceWorkerPath(basePath), {
        scope: normalizeBasePath(basePath),
        updateViaCache: "none",
      })
      .then(monitorRegistration)
      .catch(() => {
        // Offline capability is optional on browsers without service-worker support.
      });
  };

  if (browserWindow.document.readyState === "complete") {
    register();
    return;
  }

  browserWindow.addEventListener("load", register, { once: true });
}
